import { useEffect, useRef, useState } from 'react'
import type { VerifyLoadingStepDef } from '../utils/verifyLoadingConfig'

const MIN_VISIBLE_MS = 350
const DEMO_AUTO_CONTINUE_MS = 6000
const STEP_ADVANCE_MS = 520

type Options = {
  sessionKey: number
  active: boolean
  succeeded: boolean
  failed: boolean
  steps: VerifyLoadingStepDef[]
  onReveal: () => void
  /** When set, auto-continue after ready screen. null = wait for user (Peržiūrėti ataskaitą). */
  autoContinueMs?: number | null
}

export type VerifyLoadingStepVisual = 'pending' | 'active' | 'done' | 'error'

export function useVerifyLoadingProgress({
  sessionKey,
  active,
  succeeded,
  failed,
  steps,
  onReveal,
  autoContinueMs = null,
}: Options) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [completedThrough, setCompletedThrough] = useState(-1)
  const [readyMessage, setReadyMessage] = useState(false)
  const startMsRef = useRef(0)
  const revealedRef = useRef(false)

  useEffect(() => {
    if (!active) return
    revealedRef.current = false
    startMsRef.current = performance.now()
    setActiveIndex(Math.min(1, steps.length - 1))
    setCompletedThrough(0)
    setReadyMessage(false)
  }, [sessionKey, active, steps.length])

  useEffect(() => {
    if (!active || succeeded || failed) return
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => {
        const maxWhileWaiting = Math.max(0, steps.length - 2)
        return Math.min(prev + 1, maxWhileWaiting)
      })
      setCompletedThrough((prev) => {
        const maxWhileWaiting = Math.max(0, steps.length - 2)
        return Math.min(prev + 1, maxWhileWaiting)
      })
    }, STEP_ADVANCE_MS)
    return () => window.clearInterval(timer)
  }, [active, succeeded, failed, steps.length])

  useEffect(() => {
    if (!succeeded || revealedRef.current) return
    const elapsed = performance.now() - startMsRef.current
    const waitMin = Math.max(0, MIN_VISIBLE_MS - elapsed)
    const lastIndex = steps.length - 1

    let autoTimer: number | undefined
    const timer = window.setTimeout(() => {
      setCompletedThrough(lastIndex)
      setActiveIndex(lastIndex)
      setReadyMessage(true)
      if (autoContinueMs != null && autoContinueMs > 0) {
        autoTimer = window.setTimeout(() => {
          if (revealedRef.current) return
          revealedRef.current = true
          onReveal()
        }, autoContinueMs)
      }
    }, waitMin)

    return () => {
      window.clearTimeout(timer)
      if (autoTimer != null) window.clearTimeout(autoTimer)
    }
  }, [succeeded, onReveal, steps.length, autoContinueMs])

  function stepVisual(index: number): VerifyLoadingStepVisual {
    if (failed && index === activeIndex) return 'error'
    if (readyMessage && index <= activeIndex) return 'done'
    if (index < activeIndex) return 'done'
    if (index === activeIndex) return failed ? 'error' : 'active'
    return 'pending'
  }

  const statusStepIndex = failed
    ? activeIndex
    : readyMessage
      ? steps.length - 1
      : activeIndex

  return {
    activeIndex,
    completedThrough,
    readyMessage,
    statusStepIndex,
    stepVisual,
  }
}
